/**
 * Extracts a ProblemDetails or ValidationProblemDetails object from an error
 * thrown by NSwag apiClient (ApiException), Axios, or raw JSON.
 */
export const getProblemDetails = (error: any): any => {
    if (!error) return null;

    // Check NSwag ApiException.result, Axios error.response.data, or direct object
    let target = error?.result ?? error?.response?.data ?? error;

    if (typeof target === 'string') {
        try {
            target = JSON.parse(target);
        } catch {
            return null;
        }
    }

    return target;
};

/**
 * Extracts a map of field validation errors from a standard .NET ValidationProblemDetails.
 */
export const getValidationErrors = (error: any): Record<string, string[]> => {
    const details = getProblemDetails(error);
    if (!details || typeof details !== 'object') return {};

    if (details.errors && typeof details.errors === 'object') {
        return details.errors;
    }

    return {};
};

/**
 * Extracts the first validation error message for a specific field name (case-insensitive)
 * from a standard .NET ValidationProblemDetails response.
 */
export const getFieldError = (error: any, fieldName: string): string | null => {
    const errors = getValidationErrors(error);
    const targetKey = fieldName.toLowerCase();

    for (const [key, value] of Object.entries(errors)) {
        if (key.toLowerCase() === targetKey && Array.isArray(value) && value.length > 0) {
            return value[0];
        }
    }

    return null;
};

/**
 * Extracts a user-friendly error message from any error, prioritizing standard .NET
 * ValidationProblemDetails errors, detail, title, and standard Error message.
 */
export const getErrorMessage = (error: any): string => {
    const details = getProblemDetails(error);

    if (details && typeof details === 'object') {
        // 1. ValidationProblemDetails.errors dictionary
        if (details.errors && typeof details.errors === 'object' && Object.keys(details.errors).length > 0) {
            const messages = Object.values(details.errors).flat().filter(Boolean) as string[];
            if (messages.length > 0) {
                return messages.join('\n');
            }
        }

        // 2. ProblemDetails.detail
        if (typeof details.detail === 'string' && details.detail.trim().length > 0) {
            return details.detail;
        }

        // 3. ProblemDetails.title
        if (typeof details.title === 'string' && details.title.trim().length > 0) {
            return details.title;
        }
    }

    if (typeof error?.message === 'string' && error.message.trim().length > 0) {
        return error.message;
    }

    return 'An unknown error occurred. Please try again later.';
};